import { Spine } from '@esotericsoftware/spine-pixi-v8';
import '@esotericsoftware/spine-pixi-v8/dist/assets/atlasLoader.js';
import '@esotericsoftware/spine-pixi-v8/dist/assets/skeletonLoader.js';
import { Application, Assets } from 'pixi.js';
import { useCallback, useEffect, useRef } from 'react';

export type UsePixiSpineAnimationProps = {
  jsonUrl: string;
  atlasUrl: string;
  animationName: string;
  scale?: number;
  x?: number;
  y?: number;
  speed?: number;
};

export const usePixiSpineAnimation = ({
  jsonUrl,
  atlasUrl,
  animationName,
  scale = 0.2,
  x = 0,
  y = 0,
  speed = 1,
}: UsePixiSpineAnimationProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const appRef = useRef<Application | null>(null);
  const spineRef = useRef<Spine | null>(null);

  const updateSpineTransform = useCallback(
    (spine: Spine, w: number, h: number) => {
      const baseScale = Math.min(w, h) / 360;

      spine.scale.set(baseScale * scale);
      spine.x = w / 2 + x;
      spine.y = h - 20 + y;
    },
    [scale, x, y]
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let cancelled = false;
    let resizeObserver: ResizeObserver | null = null;
    let lastResizeTime = 0;
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastWidth = 0;
    let lastHeight = 0;

    const init = async () => {
      const { width, height } = container.getBoundingClientRect();

      const app = new Application();
      await app.init({ width, height, backgroundAlpha: 0 });

      // biome-ignore lint/nursery/noUnnecessaryConditions: async race condition check
      if (cancelled) {
        app.destroy(true);
        return;
      }

      container.appendChild(app.canvas);
      appRef.current = app;

      try {
        const skeletonAlias = `skeleton:${jsonUrl}`;
        const atlasAlias = `atlas:${atlasUrl}`;

        // Check cache first, only load if not already loaded
        if (!Assets.get(skeletonAlias)) {
          await Assets.load({ alias: skeletonAlias, src: jsonUrl });
        }
        if (!Assets.get(atlasAlias)) {
          await Assets.load({ alias: atlasAlias, src: atlasUrl });
        }

        // biome-ignore lint/nursery/noUnnecessaryConditions: async race condition check
        if (cancelled) {
          return;
        }

        const spine = Spine.from({
          skeleton: skeletonAlias,
          atlas: atlasAlias,
        });
        updateSpineTransform(spine, width, height);
        spine.state.setAnimation(0, animationName, true);
        spine.state.timeScale = speed;
        spine.state.data.defaultMix = 0.2;

        app.stage.addChild(spine);
        spineRef.current = spine;

        // handles wrapper container resize (throttled to prevent blink)
        const THROTTLE_MS = 150;

        const doResize = (rect: DOMRectReadOnly) => {
          // Skip if dimensions too small or unchanged
          if (rect.width < 10 || rect.height < 10) return;
          if (rect.width === lastWidth && rect.height === lastHeight) return;

          lastWidth = rect.width;
          lastHeight = rect.height;
          lastResizeTime = Date.now();
          app.renderer.resize(rect.width, rect.height);
          if (spineRef.current) {
            updateSpineTransform(spineRef.current, rect.width, rect.height);
          }
        };

        resizeObserver = new ResizeObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;

          const now = Date.now();
          const timeSinceLastResize = now - lastResizeTime;

          if (timeSinceLastResize >= THROTTLE_MS) {
            // Enough time passed, resize immediately
            doResize(entry.contentRect);
          } else {
            // Schedule resize for end of throttle window
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              doResize(entry.contentRect);
            }, THROTTLE_MS - timeSinceLastResize);
          }
        });
        resizeObserver.observe(container);
      } catch (err) {
        console.error('[PIXI-SPINE] Failed to load:', err);
      }
    };

    init();

    return () => {
      cancelled = true;
      resizeObserver?.disconnect();
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
        spineRef.current = null;
      }
    };
  }, [jsonUrl, atlasUrl, animationName, speed, updateSpineTransform]);

  const changeAnimation = useCallback((name: string) => {
    spineRef.current?.state.setAnimation(0, name, true);
  }, []);

  const pauseAnimation = useCallback(() => {
    if (spineRef.current) {
      spineRef.current.state.timeScale = 0;
    }
  }, []);

  const playAnimation = useCallback(() => {
    if (spineRef.current) {
      spineRef.current.state.timeScale = speed;
    }
  }, [speed]);

  return {
    containerRef,
    spineRef,
    changeAnimation,
    pauseAnimation,
    playAnimation,
  };
};
